import { Controller } from "@hotwired/stimulus"

// Staging-mode avatar controller. Does NOT save on its own — exposes a staged
// File or a removal flag for the parent profile-edit-mode controller to send
// in a single multipart PATCH on Done. On Cancel, the parent calls revert().
export default class extends Controller {
  static targets = [
    "fileInput",
    "originalImage",
    "preview",
    "mono",
    "uploadLabel",
    "removeButton",
    "nameInput",
    "swatch",
    "swatchPicker",
    "hueInput",
  ]

  static values = {
    hasOriginal: Boolean,
  }

  connect() {
    this.stagedFile = null
    this.stagedRemove = false
    this.previewUrl = null
  }

  disconnect() {
    this.releasePreviewUrl()
  }

  // ---- Public API used by profile-edit-mode controller -----------------

  hasStagedChange() {
    return this.stagedFile !== null || this.stagedRemove
  }

  getStagedFile() {
    return this.stagedFile
  }

  isMarkedForRemoval() {
    return this.stagedRemove
  }

  // Restore the avatar UI to its at-load state (used by Cancel and after a
  // successful commit, just before the page reloads).
  revert() {
    this.releasePreviewUrl()
    this.stagedFile = null
    this.stagedRemove = false
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
    this.applyState()
  }

  // ---- User actions ----------------------------------------------------

  stageUpload(e) {
    e.preventDefault()
    const file = this.fileInputTarget.files[0]
    if (!file) return

    const maxSize = parseInt(this.element.dataset.avatarMaxSize || "5242880", 10)
    const acceptedTypes = (this.element.dataset.avatarAcceptedTypes || "")
      .split(",").map(t => t.trim()).filter(Boolean)

    const allowsWildcard = acceptedTypes.includes("image/*")
    const isAllowedType =
      acceptedTypes.includes(file.type) || (allowsWildcard && file.type.startsWith("image/"))

    if (acceptedTypes.length && !isAllowedType) {
      this.showNotification("Avatar must be a JPG, PNG, GIF, or WebP image.")
      this.fileInputTarget.value = ""
      return
    }

    if (file.size > maxSize) {
      this.showNotification("Avatar must be smaller than 5MB.")
      this.fileInputTarget.value = ""
      return
    }

    this.releasePreviewUrl()
    this.stagedFile = file
    this.stagedRemove = false
    this.previewUrl = URL.createObjectURL(file)
    this.applyState()
    this.notifyDirty()
  }

  // Mirrors AvatarsHelper#avatar_initials_for so the mono fallback updates
  // live as the user types their name during onboarding.
  refreshInitials() {
    if (!this.hasMonoTarget || !this.hasNameInputTarget) return
    const parts = this.nameInputTarget.value.trim().split(/\s+/).slice(0, 2)
    this.monoTarget.textContent = parts.map(p => (p[0] || "").toUpperCase()).join("")
  }

  // Mirrors AvatarsHelper#avatar_gradient_for_hue. Updates the mono fallback
  // immediately and writes the chosen hue into the hidden form field.
  selectHue(e) {
    const hue = parseInt(e.currentTarget.dataset.avatarHue, 10)
    if (Number.isNaN(hue)) return
    if (this.hasMonoTarget) {
      this.monoTarget.style.background = `linear-gradient(135deg, oklch(75% 0.06 ${hue}), oklch(55% 0.10 ${hue + 30}))`
    }
    if (this.hasHueInputTarget) this.hueInputTarget.value = hue
    this.swatchTargets.forEach(s => {
      const selected = parseInt(s.dataset.avatarHue, 10) === hue
      s.classList.toggle("is-selected", selected)
      s.setAttribute("aria-checked", selected ? "true" : "false")
    })
  }

  stageRemove() {
    if (this.stagedFile) {
      // User staged an upload then changed their mind: just clear the upload.
      this.releasePreviewUrl()
      this.stagedFile = null
      if (this.hasFileInputTarget) this.fileInputTarget.value = ""
      this.applyState()
      this.notifyDirty()
      return
    }
    if (!this.hasOriginalValue) return // nothing to remove
    this.stagedRemove = true
    this.applyState()
    this.notifyDirty()
  }

  // ---- Rendering -------------------------------------------------------

  applyState() {
    const showingPreview = this.stagedFile !== null
    const showingRemoved = this.stagedRemove
    const showingOriginal = !showingPreview && !showingRemoved && this.hasOriginalValue

    if (this.hasOriginalImageTarget) this.originalImageTarget.hidden = !showingOriginal
    if (this.hasPreviewTarget) {
      if (showingPreview) {
        this.previewTarget.style.backgroundImage = `url('${this.previewUrl}')`
        this.previewTarget.hidden = false
      } else {
        this.previewTarget.hidden = true
        this.previewTarget.style.backgroundImage = ""
      }
    }
    if (this.hasMonoTarget) this.monoTarget.hidden = showingOriginal || showingPreview

    // After staging an upload, the chip should always say "Replace" (a file is present).
    // After staging a removal (no file present), it should say "Upload".
    if (this.hasUploadLabelTarget) {
      this.uploadLabelTarget.textContent = (showingPreview || showingOriginal) ? "Replace" : "Upload"
    }

    // The Remove button is meaningful when there's something to remove
    // (original avatar, or a staged upload).
    if (this.hasRemoveButtonTarget) {
      const removable = (this.hasOriginalValue && !this.stagedRemove) || showingPreview
      this.removeButtonTarget.hidden = !removable
    }

    // The hue picker only matters while the mono fallback is showing.
    if (this.hasSwatchPickerTarget) {
      this.swatchPickerTarget.hidden = showingOriginal || showingPreview
    }
  }

  releasePreviewUrl() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = null
    }
  }

  notifyDirty() {
    this.dispatch("staged", { bubbles: true })
  }

  // ---- Notifications (kept from the previous controller) ---------------

  showNotification(message, variant = "error") {
    const existing = document.querySelector(".notification")
    if (existing) existing.remove()

    const notification = document.createElement("div")
    notification.className = `notification notification-${variant}`
    notification.setAttribute("data-controller", "notification")
    notification.setAttribute("data-action", "click->notification#dismiss")
    notification.innerHTML = `
      <i class="fas ${variant === "success" ? "fa-check-circle" : "fa-exclamation-circle"}"></i>
      <span>${message}</span>
      <button class="notification-close" data-action="click->notification#dismiss">
        <i class="fas fa-times"></i>
      </button>
    `
    document.body.appendChild(notification)
  }
}
